import Button from "@ui/button/Button"
import { EntityInput } from "./EntityInput"
import { askConfirm } from "./confirm"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { systemErrorMessage, systemPost, type ISystemApiError, type TSystemApiResponse } from "src/services/superAdminApi"

import { T } from "../adminTheme"
import { Avatar, Callout, Check, DangerZone, Disclosure, Fields, FilterBar, linkRow, Muted, OpenCell, Page, Pager, Panel, Row, SearchField, State, StatusBadge, Table } from "./adminUi"
import { UserOverviewData } from "./UserOverviewData"
import { CardholderFlow as FlowStatus, CardholderStatuses, holderAction, stageLabel, type ICardholderFlow } from "./CardholderFlow"
import { SuperAdminManualKyc } from "./SuperAdminManualKyc"

const PAGE_SIZE = 50
const HTTP_CONFLICT = 409

export interface ISystemCardholderRow {
	uid: string
	user_uid: string
	user_name?: string
	user_email?: string
	company_uid: string
	company_name?: string
	provider_code: string
	provider_name?: string
	issuer?: number
	provider_holder_id?: string
	status?: string
	type?: string
	user_kyc_status?: string
	kyc_status?: string
	kyc_uuid?: string
	cards_count?: number
	shares_issuer_holder_with?: number
	donor_ready?: boolean
	donor_blockers?: string[]
	user_role?: string
	user_status?: string
	user_company_status?: string
	flow?: ICardholderFlow
	[key: string]: unknown
}

interface ICardholderDetails extends ISystemCardholderRow {
	our_kyc?: unknown
	cards?: unknown[]
	siblings?: unknown[]
	events?: unknown[]
}

interface IListResponse {
	total: number
	limit: number
	offset: number
	rows: ISystemCardholderRow[]
	order?: "asc" | "desc"
}

interface IMutationResult {
	mode: "copy" | "move"
	target_holder_uid: string
	overwritten?: boolean
	kyc_copied?: boolean
	issuer_check?: { checked?: boolean; found?: boolean; status?: string; note?: string }
	warnings?: string[]
	[key: string]: unknown
}

type MutationMode = "copy" | "move"

const findConflictUid = (error: ISystemApiError): string | null => {
	for (const key of ["existing_holder_uid", "target_holder_uid", "holder_uid"]) {
		const value = error[key]
		if (typeof value === "string" && value.trim()) return value
	}
	if (error.details && typeof error.details === "object") return findConflictUid(error.details as ISystemApiError)
	const text = `${error.message ?? ""} ${typeof error.details === "string" ? error.details : ""}`
	return text.match(/(?:existing|target)?[\s_-]*holder[\s_-]*uid[^A-Za-z0-9_-]*["']?([A-Za-z0-9_-]{4,})/i)?.[1] ?? null
}

const isOverwriteConflict = (error: ISystemApiError) => {
	const text = `${error.message ?? ""} ${typeof error.details === "string" ? error.details : ""}`.toLowerCase()
	return Boolean(findConflictUid(error)) && (Number(error.code) === HTTP_CONFLICT || /already|conflict|existing/.test(text))
}

const performCardholderMutation = async (
	token: string,
	mode: MutationMode,
	payload: Record<string, unknown>,
	confirmOverwrite: (existingUid: string) => boolean | Promise<boolean>
): Promise<TSystemApiResponse<IMutationResult>> => {
	const first = await systemPost<IMutationResult>(token, `system/cardholders/${mode}`, { ...payload, force: false })
	if (!first.error || !isOverwriteConflict(first.error)) return first
	const existingUid = findConflictUid(first.error)
	if (!existingUid || !(await confirmOverwrite(existingUid))) return first
	return systemPost<IMutationResult>(token, `system/cardholders/${mode}`, { ...payload, force: true })
}

const JsonBlock = ({ value, label }: { value: unknown; label: string }) => (
	<Disclosure label={label}>
		<UserOverviewData value={value} />
	</Disclosure>
)

/** "address.city_code" → "city code" */
const fieldName = (path: string) => path.split(".").pop()!.replaceAll("_", " ")

/** Clicks and keys inside these cells must not open the row. */
const stop = { onClick: (event: React.SyntheticEvent) => event.stopPropagation(), onKeyDown: (event: React.SyntheticEvent) => event.stopPropagation() }

const HolderRow = ({ row, issuerLabel, loading, active, onOpen, onNext }: { row: ISystemCardholderRow; issuerLabel?: string; loading: boolean; active: boolean; onOpen: () => void; onNext: () => void }) => {
	const flow = row.flow
	const action = holderAction(flow, row.user_kyc_status)
	const hint = flow?.next_action || flow?.description
	const missing = flow?.missing_evaluated ? flow.missing_for_issuer ?? [] : []
	const accessProblem = row.user_status !== "active" || row.user_company_status === "disabled"
	const name = row.user_name || row.user_email || row.user_uid
	return <tr {...linkRow(onOpen)} data-active={active || undefined}>
		<td>
			<Row style={{ gap: 10, flexWrap: "nowrap" }}>
				<Avatar name={name} size={28} />
				<div style={{ minWidth: 0 }}>
					<span style={{ fontWeight: 600 }}>{name}</span>
					<small>{row.company_name || row.company_uid}</small>
					{accessProblem && <small style={{ color: T.danger }}>User {row.user_status || "unknown"} · company access {row.user_company_status || "unknown"}</small>}
				</div>
			</Row>
		</td>
		<td><span style={{ fontWeight: 600 }}>{issuerLabel || row.provider_name || row.provider_code}</span><small>{row.type || "—"}</small></td>
		<td>
			<StatusBadge value={flow ? stageLabel(flow) : row.status} />
			{hint && <small>{hint}</small>}
			{(missing.length > 0 || flow?.blocking) && <div {...stop} style={{ marginTop: 4 }}>
				<Disclosure flat label={<span style={{ color: T.warn }}>Why? {missing.length ? `${missing.length} field${missing.length === 1 ? "" : "s"} missing` : "blocked"}</span>}>
					<small style={{ color: T.warn }}>{missing.length ? `Missing: ${missing.map(fieldName).join(", ")}` : `Blocking: ${flow?.blocking}`}</small>
				</Disclosure>
			</div>}
		</td>
		<td>
			<div style={{ display: "grid", gap: 3, justifyItems: "start" }}>
				<span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Muted style={{ fontSize: 12.5 }}>Issuer</Muted><StatusBadge value={row.kyc_status || "unknown"} /></span>
				<span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Muted style={{ fontSize: 12.5 }}>Ours</Muted><StatusBadge value={row.user_kyc_status || "unknown"} /></span>
			</div>
		</td>
		<td>{row.cards_count ?? 0} card{row.cards_count === 1 ? "" : "s"}<small title={(row.donor_blockers ?? []).join("\n")}><StatusBadge value={row.donor_ready ? "Can be copied" : "Cannot be copied"} /></small></td>
		<td {...stop} style={{ whiteSpace: "nowrap" }}>{action && <Button variant='secondarySmall' disabled={loading} onClick={onNext}>{action}</Button>}</td>
		<OpenCell />
	</tr>
}

export const SuperAdminCardholders = ({ token }: { token: string }) => {
	const [rows, setRows] = useState<ISystemCardholderRow[]>([])
	const [total, setTotal] = useState(0)
	const [offset, setOffset] = useState(0)
	const [search, setSearch] = useState("")
	const [userUid, setUserUid] = useState("")
	const [donorsOnly, setDonorsOnly] = useState(false)
	const [order, setOrder] = useState<"asc" | "desc">("desc")
	const [filters, setFilters] = useState({ status: "", type: "", kyc_status: "" })
	const [loading, setLoading] = useState(false)
	const [selected, setSelected] = useState<ICardholderDetails | null>(null)
	const [targetUserUid, setTargetUserUid] = useState("")
	const [targetCompanyUid, setTargetCompanyUid] = useState("")
	const [copyKyc, setCopyKyc] = useState(true)
	const [verify, setVerify] = useState(true)
	const [operationLoading, setOperationLoading] = useState<MutationMode | null>(null)
	const [manualHolder, setManualHolder] = useState<ISystemCardholderRow | null>(null)
	const [result, setResult] = useState<IMutationResult | null>(null)
	const [issuerLabels, setIssuerLabels] = useState<Record<number, string>>({})
	const detailsRef = useRef<HTMLDivElement>(null)
	// Details render below the list; bring them into view when a holder is opened.
	useEffect(() => { detailsRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }) }, [selected?.uid])
	useEffect(() => {
		void systemPost<{ issuers?: Array<{ value: number; label: string }> }>(token, "system/transactions/filters", {}).then((response) => {
			if (response.data?.issuers) setIssuerLabels(Object.fromEntries(response.data.issuers.map((item) => [item.value, item.label])))
		})
	}, [token])

	const loadList = useCallback(async (nextOffset: number) => {
		setLoading(true)
		const response = await systemPost<IListResponse>(token, "system/cardholders/list", {
			search: search.trim(), user_uid: userUid.trim(), donors_only: donorsOnly, order,
			status: filters.status, type: filters.type, kyc_status: filters.kyc_status,
			limit: PAGE_SIZE, offset: nextOffset
		})
		setLoading(false)
		if (response.error) return toast.error(systemErrorMessage(response.error))
		setRows(response.data.rows ?? [])
		setTotal(Number(response.data.total ?? 0))
		setOffset(Number(response.data.offset ?? nextOffset))
	}, [donorsOnly, filters, order, search, token, userUid])

	useEffect(() => { void loadList(0) }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

	const openDetails = async (uid: string) => {
		setLoading(true)
		setResult(null)
		const response = await systemPost<ICardholderDetails>(token, "system/cardholders/get", { uid })
		setLoading(false)
		if (response.error) return toast.error(systemErrorMessage(response.error))
		setSelected(response.data)
	}

	const confirmOverwrite = (existingUid: string) => {
		return askConfirm({ title: "Overwrite the existing card holder?", body: `Target already has card holder ${existingUid}. Overwrite it?`, confirmLabel: "Overwrite", danger: true })
	}

	const mutate = async (mode: MutationMode) => {
		if (!selected || !targetUserUid.trim()) return toast.warning("Enter the target user UID")
		setOperationLoading(mode)
		setResult(null)
		const payload = mode === "copy"
			? { source_holder_uid: selected.uid, target_user_uid: targetUserUid.trim(), target_company_uid: targetCompanyUid.trim(), copy_kyc: copyKyc, verify }
			: { holder_uid: selected.uid, target_user_uid: targetUserUid.trim(), target_company_uid: targetCompanyUid.trim(), copy_kyc: copyKyc }
		const response = await performCardholderMutation(token, mode, payload, confirmOverwrite)
		setOperationLoading(null)
		if (response.error) return toast.error(systemErrorMessage(response.error))
		setResult(response.data)
		toast.success(`Card holder ${mode} completed`)
		void loadList(offset)
	}


	const nextStep = async (row: ISystemCardholderRow) => {
		if (holderAction(row.flow, row.user_kyc_status) === "Refresh status") {
			await openDetails(row.uid)
			void loadList(offset)
		} else setManualHolder(row)
	}

	if (manualHolder) return <Page>
		<Row><Button variant='secondarySmall' onClick={() => { setManualHolder(null); void loadList(offset); if (selected) void openDetails(selected.uid) }}>← Back to card holders</Button></Row>
		<SuperAdminManualKyc key={manualHolder.uid} token={token} initialCardHolderUid={manualHolder.uid} />
	</Page>

	const selectedAction = selected ? holderAction(selected.flow, selected.user_kyc_status) : null
	const text = (name: string, value: string, placeholder: string, onChange: (value: string) => void) =>
		<input name={name} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
	return (
		<Page>
			<Panel title='Card holders' hint='Search by holder, user, KYC, phone, email or company.'
				actions={<Button variant='primarySmall' onClick={() => void loadList(0)} disabled={loading}>Search</Button>}>
				<div style={{ display: "grid", gap: 14 }}>
					<SearchField label='Search card holders' placeholder='Holder, user, KYC, phone, email or company' value={search} onChange={setSearch} />
					<FilterBar>
						<label>User<EntityInput token={token} kind='user' name='userUid' value={userUid} placeholder='All holders for one user' onChange={setUserUid} /></label>
						<label>Order<select name='holder_order' value={order} onChange={(event) => setOrder(event.target.value === "asc" ? "asc" : "desc")}><option value='desc'>Newest first</option><option value='asc'>Oldest first</option></select></label>
						<label>Issuer audit{text("status", filters.status, "All statuses", (value) => setFilters((current) => ({ ...current, status: value })))}</label>
						<label>Type{text("type", filters.type, "All types", (value) => setFilters((current) => ({ ...current, type: value })))}</label>
						<label>Issuer KYC{text("kyc_status", filters.kyc_status, "All KYC statuses", (value) => setFilters((current) => ({ ...current, kyc_status: value })))}</label>
					</FilterBar>
					<Check checked={donorsOnly} onChange={(event) => setDonorsOnly(event.target.checked)}>Can be copied only</Check>
				</div>
			</Panel>

			<Panel title='Results' hint={`${rows.length} of ${total} card holders`}>
				<State loading={loading && rows.length === 0} empty={rows.length === 0 && !loading} emptyText='No card holders match these filters.' />
				{rows.length > 0 && <div style={{ opacity: loading ? 0.6 : 1, transition: `opacity .16s ${T.ease}` }}><Table>
					<thead><tr><th>Card holder</th><th>Issuer</th><th>Status and next step</th><th>KYC</th><th>Cards</th><th /><th /></tr></thead>
					<tbody>{rows.map((row) => <HolderRow key={row.uid} row={row} active={selected?.uid === row.uid} issuerLabel={row.issuer ? issuerLabels[row.issuer] : undefined} loading={loading} onOpen={() => void openDetails(row.uid)} onNext={() => void nextStep(row)} />)}</tbody>
				</Table></div>}
				{total > PAGE_SIZE && <Pager total={total} from={offset + 1} to={offset + rows.length} page={Math.floor(offset / PAGE_SIZE)} pages={Math.ceil(total / PAGE_SIZE)} onPage={(next) => { if (!loading) void loadList(next * PAGE_SIZE) }} />}
			</Panel>

			{selected && <div ref={detailsRef} style={{ display: "grid", gap: 16, minWidth: 0, animation: `bo-rise .28s ${T.ease} both` }}>
				<Panel title={selected.user_name || selected.user_email || selected.user_uid} hint={<><code>{selected.uid}</code> · {selected.provider_code} · {selected.provider_holder_id ? <code>{selected.provider_holder_id}</code> : "No provider holder ID"}</>}
					actions={<Button variant='secondarySmall' onClick={() => setSelected(null)}>Close</Button>}>
					<CardholderStatuses holder={selected} />
					<div style={{ marginTop: 16 }}><FlowStatus flow={selected.flow} ourKycStatus={selected.user_kyc_status} /></div>
					{selectedAction && <Row style={{ marginTop: 16 }}><Button variant='primarySmall' disabled={loading} onClick={() => void nextStep(selected)}>{selectedAction}</Button></Row>}
				</Panel>
				<Panel title='Related data' hint='Raw records linked to this card holder.'>
					<div style={{ display: "grid", gap: 10 }}><JsonBlock label={`Cards (${selected.cards?.length ?? 0})`} value={selected.cards ?? []} /><JsonBlock label={`Sibling holders (${selected.siblings?.length ?? 0})`} value={selected.siblings ?? []} /><JsonBlock label={`Related events (${selected.events?.length ?? 0})`} value={selected.events ?? []} /><JsonBlock label='Our KYC verdict' value={selected.our_kyc ?? null} /></div>
				</Panel>
				<DangerZone title='Copy or move this card holder' hint='Copying creates a holder for another user. Moving hands this holder row to another user.'>
					<Fields>
						<label>Target user<EntityInput token={token} kind='user' name='targetUserUid' value={targetUserUid} placeholder='Required: name, e-mail or UID' onChange={setTargetUserUid} /></label>
						<label>Target company<EntityInput token={token} kind='company' name='targetCompanyUid' value={targetCompanyUid} placeholder='Optional — user company by default' onChange={setTargetCompanyUid} /></label>
					</Fields>
					<Row style={{ gap: 20 }}><Check checked={copyKyc} onChange={(event) => setCopyKyc(event.target.checked)}>Copy our KYC verdict</Check><Check checked={verify} onChange={(event) => setVerify(event.target.checked)}>Verify holder with issuer</Check></Row>
					<Callout tone='warn'>Moving changes ownership of this holder row only. Already issued cards remain with the previous owner; only new cards belong to the target user.</Callout>
					<Row><Button variant='primarySmall' disabled={Boolean(operationLoading)} onClick={() => void mutate("copy")}>{operationLoading === "copy" ? "Copying…" : "Copy card holder"}</Button><Button variant='secondarySmall' disabled={Boolean(operationLoading)} onClick={() => void mutate("move")}>{operationLoading === "move" ? "Moving…" : "Move card holder"}</Button></Row>
					{result && <Callout role='status' tone='success' title={`${result.mode === "move" ? "Move" : "Copy"} completed · ${result.target_holder_uid}`}>
						{result.issuer_check?.checked && <div>Issuer check: {result.issuer_check.found ? "found" : "not found"}{result.issuer_check.note ? ` — ${result.issuer_check.note}` : ""}</div>}
						{(result.warnings ?? []).length > 0 && <div style={{ marginTop: 6, color: T.warn }}><strong>Warnings</strong><ul>{result.warnings!.map((warning, index) => <li key={index}>{warning}</li>)}</ul></div>}
					</Callout>}
				</DangerZone>
			</div>}
		</Page>
	)
}
